import { useCallback, useEffect, useRef, useState } from 'react';
import { isBrowser } from '../utils';
const historyLength = 32;
const historyMaxAge = 512;
const thresholdPixels = 1;
export function useScrollDirection({ scrollingElement, onChange, } = {}) {
    const currentScrollDirection = useRef('');
    const [delayedScrollDirection, setDelayedScrollDirection] = useState('');
    const setScrollDirection = useCallback((newScrollDirection) => {
        const changedDirection = currentScrollDirection.current !== newScrollDirection;
        currentScrollDirection.current = newScrollDirection;
        setDelayedScrollDirection(newScrollDirection);
        if (changedDirection)
            onChange?.(newScrollDirection);
    }, [onChange]);
    const [element, eventEmitter] = scrollingElement
        ? [scrollingElement, scrollingElement]
        : isBrowser
            ? [window.document.scrollingElement, window]
            : [null, null];
    useEffect(() => {
        if (!element || !eventEmitter)
            return;
        let eventHistory = Array(historyLength);
        let pivotPoint = { t: 0, y: 0 };
        const tick = (event) => {
            const latestHistoryEntry = eventHistory[0];
            if (!latestHistoryEntry || event !== latestHistoryEntry.event)
                return;
            const { t, y } = latestHistoryEntry;
            if (y === 0) {
                setScrollDirection('');
                pivotPoint = { t, y };
                return;
            }
            const furthest = currentScrollDirection.current === 'down' ? Math.max : Math.min;
            if (y === furthest(pivotPoint.y, y)) {
                pivotPoint = { t, y };
                return;
            }
            const cutoffTime = t - historyMaxAge;
            if (cutoffTime > pivotPoint.t) {
                pivotPoint.y = y;
                eventHistory.filter(Boolean).forEach(({ y, t }) => {
                    if (t > cutoffTime)
                        pivotPoint.y = furthest(pivotPoint.y, y);
                });
            }
            if (Math.abs(y - pivotPoint.y) > thresholdPixels) {
                pivotPoint = { t, y };
                const newScrollDirection = currentScrollDirection.current === 'down' ? 'up' : 'down';
                setScrollDirection(newScrollDirection);
            }
        };
        const onScroll = (event) => {
            if (document.body.getAttribute('data-dialog-prevent-body-scroll')) {
                return;
            }
            const yMax = element.scrollHeight - element.clientHeight;
            let y = element.scrollTop;
            y = Math.max(0, y);
            y = Math.min(yMax, y);
            eventHistory.pop();
            eventHistory.unshift({ event, t: event.timeStamp, y });
            requestAnimationFrame(() => {
                tick(event);
            });
        };
        eventEmitter.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            eventEmitter.removeEventListener('scroll', onScroll);
        };
    }, [setScrollDirection, element, eventEmitter]);
    return delayedScrollDirection;
}
//# sourceMappingURL=useScrollDirection.js.map